import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
} from "@mui/material";
import { ResponsivePie } from "@nivo/pie";
import axios from "axios";

const API_ENDPOINT = "https://figazuresql.swancreate.com/api/swa/vw_CurrentInventoryCounts";

const InventoryValueReport = () => {
    const [inventoryData, setInventoryData] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [isLoading, setIsLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchInventoryData = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(API_ENDPOINT);
            const filtered = response.data.filter(
                (item) => item.CreatedOn.slice(0, 7) === selectedMonth
            );
            setInventoryData(filtered);
        } catch (error) {
            console.error("Failed to fetch inventory data:", error);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchInventoryData();
    }, [selectedMonth]);

    const pieData = inventoryData.reduce((acc, item) => {
        const existing = acc.find((d) => d.id === item.VendorName);
        if (existing) {
            existing.value += item.TotalCost;
        } else {
            acc.push({
                id: item.VendorName,
                label: item.VendorName,
                value: item.TotalCost,
            });
        }
        return acc;
    }, []);

    const pieDataInThousands = pieData.map((d) => ({
        ...d,
        value: parseFloat((d.value / 1000).toFixed(2)),
    }));

    const totalInventoryValue = inventoryData.reduce(
        (acc, item) => acc + item.TotalCost,
        0
    );

    const formatCurrency = (value) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
        }).format(value);

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const dateObj = new Date(dateString);
        return dateObj.toLocaleDateString("en-US");
    };

    const handlePrint = () => {
        const printContents = document.getElementById("print-area").innerHTML;
        const originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
    };

    const tableRows = inventoryData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box
            sx={{
                width: "8.5in",
                minHeight: "11in",
                margin: "auto",
                padding: "20px",
                backgroundColor: "#121212",
                color: "#ffffff",
                borderRadius: "10px",
                boxShadow: "0 4px 8px rgba(0, 255, 255, 0.3)",
                position: "relative",
            }}
        >
            <Box
                component="img"
                src="src/assets/fig_CROP.png"
                sx={{
                    position: "absolute",
                    top: "20px",
                    right: "20px",
                    width: "120px",
                    opacity: 0.15,
                }}
            />

            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
                Inventory Value Report
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel sx={{ color: "#ffffff" }}>Month</InputLabel>
                    <Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        sx={{
                            backgroundColor: "#1E1E1E",
                            color: "#ffffff",
                            border: "1px solid #00FFFF",
                        }}
                    >
                        {Array.from({ length: 12 }, (_, i) => {
                            const dateObj = new Date(new Date().getFullYear(), i);
                            const yearMonth = dateObj.toISOString().slice(0, 7);
                            return (
                                <MenuItem key={i} value={yearMonth}>
                                    {dateObj.toLocaleString("default", {
                                        month: "long",
                                        year: "numeric",
                                    })}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#00FFFF",
                            "&:hover": { backgroundColor: "#00CED1" },
                        }}
                        onClick={fetchInventoryData}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Reload Data"}
                    </Button>

                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#FFD700",
                            "&:hover": { backgroundColor: "#FFC200" },
                        }}
                        onClick={handlePrint}
                    >
                        Print
                    </Button>
                </Box>
            </Box>

            <Box
                id="print-area"
                sx={{
                    backgroundColor: "#1E1E1E",
                    padding: "20px",
                    borderRadius: "10px",
                }}
            >
                <Typography variant="h5" sx={{ color: "#00FFFF", mb: 2 }}>
                    Total Inventory Value: {formatCurrency(totalInventoryValue)}
                </Typography>

                <Box height={400} mt={4}>
                    {pieDataInThousands.length > 0 ? (
                        <ResponsivePie
                            data={pieDataInThousands}
                            margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                            innerRadius={0.5}
                            padAngle={0.7}
                            cornerRadius={3}
                            colors={{ scheme: "set2" }}
                            borderWidth={1}
                            borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                            radialLabelsSkipAngle={10}
                            radialLabelsTextColor="#ffffff"
                            radialLabelsLinkColor={{ from: "color" }}
                            sliceLabelsSkipAngle={10}
                            sliceLabelsTextColor="#ffffff"
                            legends={[
                                {
                                    anchor: "bottom",
                                    direction: "row",
                                    translateY: 56,
                                    itemWidth: 100,
                                    itemHeight: 18,
                                    itemTextColor: "#ffffff",
                                    symbolSize: 18,
                                    symbolShape: "circle",
                                },
                            ]}
                        />
                    ) : (
                        <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>
                            No data available for the selected month.
                        </Typography>
                    )}
                </Box>

                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: "bold",
                        mt: 4,
                        mb: 1,
                        textAlign: "center",
                        color: "#00FFFF",
                    }}
                >
                    Detailed Inventory Table
                </Typography>

                <TableContainer
                    component={Paper}
                    sx={{
                        backgroundColor: "#1E1E1E",
                        borderRadius: "10px",
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: "#00FFFF" }}>Site Name</TableCell>
                                <TableCell sx={{ color: "#00FFFF" }}>Product Name</TableCell>
                                <TableCell sx={{ color: "#00FFFF" }}>Units</TableCell>
                                <TableCell sx={{ color: "#00FFFF" }}>Unit Cost</TableCell>
                                <TableCell sx={{ color: "#00FFFF" }}>Total Cost</TableCell>
                                <TableCell sx={{ color: "#00FFFF" }}>Vendor</TableCell>
                                <TableCell sx={{ color: "#00FFFF" }}>Date of Last Count</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableRows.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{ color: "#ffffff" }}>{row.SiteName}</TableCell>
                                    <TableCell sx={{ color: "#ffffff" }}>{row.ProductName}</TableCell>
                                    <TableCell sx={{ color: "#ffffff" }}>{row.UNITS}</TableCell>
                                    <TableCell sx={{ color: "#ffffff" }}>
                                        {formatCurrency(row.UnitCost)}
                                    </TableCell>
                                    <TableCell sx={{ color: "#ffffff" }}>
                                        {formatCurrency(row.TotalCost)}
                                    </TableCell>
                                    <TableCell sx={{ color: "#ffffff" }}>{row.VendorName}</TableCell>
                                    <TableCell sx={{ color: "#ffffff" }}>
                                        {formatDate(row.CreatedOn)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box sx={{ "@media print": { display: "none" } }}>
                <TablePagination
                    component="div"
                    count={inventoryData.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        color: "#ffffff",
                        ".MuiSelect-icon": { color: "#ffffff" },
                        ".MuiTablePagination-toolbar": { backgroundColor: "#1E1E1E" },
                        ".MuiTablePagination-actions button": { color: "#ffffff" },
                    }}
                />
            </Box>
        </Box>
    );
};

export default InventoryValueReport;
